import React, { useState, useEffect } from 'react';
import PageApp from '../../../Page';
import {HomeOutlined, UnorderedListOutlined, CheckOutlined, DownloadOutlined} from '@ant-design/icons';
import { Table, Button, Row, Col, DatePicker, Input, Select, Switch, Typography, Divider, Form, Card, Collapse, message, } from 'antd';
import dayjs from 'dayjs';

import { getListAreas, getDepartamentos, getTiposAusentismo, getEmpleados, updateTipoIncapacidad, updateOdooLeaveType, getIntegracion, upsertAguinaldoEmpleado, 
          getEmpleadosByDepartamento, getEmpleadosByArea, getEmpleadosSinAguinaldo, getEmpleadosConAguinaldo, getEmpleadosByPuesto, getPuestos,
} from '../../../../apis/CalculoAguinaldoApi';

import { getData as getConfiguracionNomina } from '../../../../apis/ConfiguracionNominaApi';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;

const routes = [
  {
    href: '',
    title: (
      <>
        <HomeOutlined />
        <span>Inicio</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <span>Nomina</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <span>Cálculos Anuales</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Cálculo de Aguinaldo</span>
      </>
    ),
  },
];

const parseEmpleadoIds = (rango) => {
  if (!rango) return [];
  return rango
    .split(',')
    .map((x) => x.trim())
    .filter((x) => x !== '')
    .map((x) => Number(x))
    .filter((n) => !isNaN(n));
};

export default function AguinaldoView() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  const [areas, setAreas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [tiposAusentismo, setTiposAusentismoState] = useState([]);
  const [ausentismosSeleccionados, setAusentismosSeleccionados] = useState({});
  const [integracion, setIntegracion] = useState([]);
  const [empleadosSinAguinaldo, setEmpleadosSinAguinaldo] = useState([]);
  const [puestos, setPuestos] = useState([]);
  const [form] = Form.useForm();

  const exportToExcel = () => {
    try {
      const escapeHtml = (val) =>
        String(val ?? '')
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#39;');

      const fechaLabel = form.getFieldValue('fecha_antiguedad') === 'antiguedad' ? 'Antigüedad' : 'Fecha Ingreso';

      const buildRow = (r) => `
        <tr>
          <td style="border:1px solid #d9d9d9;padding:6px;">${escapeHtml(r.empleado)}</td>
          <td style="border:1px solid #d9d9d9;padding:6px;">${escapeHtml(r.nombre)}</td>
          <td style="border:1px solid #d9d9d9;padding:6px;">${escapeHtml(r.fecha_ingreso)}</td>
          <td style="border:1px solid #d9d9d9;padding:6px;text-align:center;">${escapeHtml(r.dias_considerar)}</td>
          <td style="border:1px solid #d9d9d9;padding:6px;text-align:center;">${escapeHtml(r.dias_aguinaldo)}</td>
          <td style="border:1px solid #d9d9d9;padding:6px;text-align:center;">${escapeHtml(r.faltas)}</td>
          <td style="border:1px solid #d9d9d9;padding:6px;text-align:center;">${escapeHtml(r.incap)}</td>
        </tr>`;

      const headerRow = `
        <tr style="background:#fafafa;font-weight:600;">
          <th style="border:1px solid #d9d9d9;padding:6px;">ID</th>
          <th style="border:1px solid #d9d9d9;padding:6px;">Nombre</th>
          <th style="border:1px solid #d9d9d9;padding:6px;">${escapeHtml(fechaLabel)}</th>
          <th style="border:1px solid #d9d9d9;padding:6px;">Días a Considerar</th>
          <th style="border:1px solid #d9d9d9;padding:6px;">Días Aguinaldo</th>
          <th style="border:1px solid #d9d9d9;padding:6px;">Faltas</th>
          <th style="border:1px solid #d9d9d9;padding:6px;">Incapacidades</th>
        </tr>`;

      const tableHtml = `
        <html>
          <head>
            <meta charset="utf-8" />
          </head>
          <body>
            <table style="border-collapse:collapse;font-family:Arial, sans-serif;font-size:12px;">
              <thead>${headerRow}</thead>
              <tbody>
                ${rows.map(buildRow).join('')}
              </tbody>
            </table>
          </body>
        </html>`;

      const blob = new Blob([tableHtml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const anio = form.getFieldValue('anio') || new Date().getFullYear();
      link.href = url;
      link.download = `calculo_aguinaldo_${anio}.xls`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      message.error('No se pudo exportar la tabla');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'empleado',
      width: 90,
      sorter: (a, b) => Number(a.empleado || 0) - Number(b.empleado || 0),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      sorter: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: () => {
        const v = form.getFieldValue('fecha_antiguedad');
        return v === 'antiguedad' ? 'Fechas Antigüedad' : 'Fecha Ingreso (IMSS)';
      },
      dataIndex: 'fecha_ingreso',
    },
    { title: 'Días a Considerar', dataIndex: 'dias_considerar', align: 'center' },
    { title: 'Días Aguinaldo', dataIndex: 'dias_aguinaldo', align: 'center' },
    { title: 'Faltas', dataIndex: 'faltas', align: 'center' },
    { title: 'Incapacidades', dataIndex: 'incap', align: 'center' },
  ];

  // Carga de catálogos al montar
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [resAreas, resDeptos, resAus, resIntegracion, resConfig, resPos] = await Promise.all([
          getListAreas(),
          getDepartamentos(),
          getTiposAusentismo(),
          getIntegracion(),
          getConfiguracionNomina(),
          getPuestos(),
        ]);

        if (resAreas?.ok) {
          setAreas(resAreas.payload || []);
        }
        if (resDeptos?.ok) {
          setDepartamentos(resDeptos.payload || []);
        }
        if (resIntegracion?.ok) {
          setIntegracion(resIntegracion.payload || []);
        }
        if (resPos?.ok) {
          setPuestos(resPos.payload || []);
        }

        if (resConfig?.ok && resConfig.payload) {
          const config = resConfig.payload;
          let fechaAntiguedadSelect = 'ingreso';
          if (config.tipo_fecha_aguinaldo === 'fecha_antiguedad') {
            fechaAntiguedadSelect = 'antiguedad';
          }else if (config.tipo_fecha_aguinaldo === 'fecha_ingreso'){
            fechaAntiguedadSelect = 'ingreso';
          }
          const defaults = {};
          if (config.tabla_integracion) {
            defaults.integracion_id = config.tabla_integracion;
          }
          if (config.tipo_fecha_aguinaldo) {
            defaults.fecha_antiguedad = fechaAntiguedadSelect;
          }
          if (Object.keys(defaults).length > 0) {
            form.setFieldsValue(defaults);
          }
        }
        if (resAus?.ok) {
          const lista = resAus.payload || [];
          setTiposAusentismoState(lista);

          // inicializar seleccionados con el campo "activo"
          const inicial = {};
          lista.forEach((item) => {
            const key = `${item.source}-${item.id}`;
            inicial[key] = !!item.activo;
          });
          setAusentismosSeleccionados(inicial);
        }
      } catch (err) {
        message.error('Error al cargar catálogos para el cálculo de aguinaldo');
      }
    };

    loadCatalogs();
  }, []);

  const handleChangeAusentismo = async (key, checked, item) => {
    const prevChecked = !!ausentismosSeleccionados[key];
    setAusentismosSeleccionados((prev) => ({
      ...prev,
      [key]: checked,
    }));
    try {
      let response = null;
      if (item.source === 'tipo_incapacidad') {
        response = await updateTipoIncapacidad(item.id, checked);
      } else if (item.source === 'odoo_leave_type') {
        response = await updateOdooLeaveType(item.odoo_id ?? item.id, checked);
      }

      message.success(`Tipo de ausentismo "${item.nombre || item.tipo}" actualizado correctamente.`);
    } catch (err) {
      message.error(`Error al actualizar el tipo de ausentismo "${item.nombre || item.tipo}".`);
      // revertir el cambio en la UI
      setAusentismosSeleccionados((prev) => ({
        ...prev,
        [key]: prevChecked,
      }));
    }
  };

  const onCalculate = async () => {
    try {
      setLoading(true);
      setRows([]);

      const values = await form.validateFields();

      let listaEmpleados = [];

      // Rango de empleados
      if (values.rango_empleados) {
        const idsEmpleados = parseEmpleadoIds(values.rango_empleados);
        if (!idsEmpleados.length) {
          message.warning("No se encontraron IDs válidos en el rango.");
          setLoading(false);
          return;
        }

        const res = await getEmpleados(idsEmpleados);
        if (!res?.ok) {
          message.error(res?.message || "Error al obtener empleados por rango.");
          setLoading(false);
          return;
        }

        listaEmpleados = res.payload || [];
      }
      // Área
      else if (values.area_id) {
        const res = await getEmpleadosByArea(values.area_id);
        if (!res?.ok) {
          message.error(res?.message || "Error al obtener empleados por área.");
          setLoading(false);
          return;
        }

        listaEmpleados = res.payload || [];
      }
      // Departamento
      else if (values.departamento_id) {
        const res = await getEmpleadosByDepartamento(values.departamento_id);
        if (!res?.ok) {
          message.error(res?.message || "Error al obtener empleados por departamento.");
          setLoading(false);
          return;
        }

        listaEmpleados = res.payload || [];
      }
      // Puesto
      else if (values.puesto) {
        const res = await getEmpleadosByPuesto(values.puesto);
        if (!res?.ok) {
          message.error(res?.message || "Error al obtener empleados por puesto.");
          setLoading(false);
          return;
        }

        listaEmpleados = res.payload || [];
      }
      // Nada seleccionado → llamar a getEmpleadosSinAguinaldo
      else {
        const anio = values.anio || new Date().getFullYear();
        const res = await getEmpleadosSinAguinaldo(anio);
        
        if (!res?.ok) {
          message.error(res?.message || "Error al obtener empleados sin aguinaldo calculado.");
          setLoading(false);
          return;
        }

        let empleadosPayload = res.payload || [];

        if (!empleadosPayload.length) {
          const res = await getEmpleadosConAguinaldo(anio);
          empleadosPayload = res.payload || [];
        }

        const idsEmpleados = empleadosPayload.map((emp) => emp.id || emp.empleado_id);

        listaEmpleados = idsEmpleados.map((empId) => ({ id: empId }));
      }

      if (!listaEmpleados.length) {
        message.warning("No se encontraron empleados para los filtros seleccionados.");
        setLoading(false);
        return;
      }

      // Preparar parámetros de cálculo
      const fechaInicial = values.fecha_inicial
        ? values.fecha_inicial.format("YYYY-MM-DD")
        : null;

      const fechaFinal = values.fecha_final
        ? values.fecha_final.format("YYYY-MM-DD")
        : null;

      const fechaIndicadaBackend =
        values.fecha_antiguedad === "antiguedad"
          ? "fecha_antiguedad"
          : "fecha_ingreso";

      const integracionId = values.integracion_id;

      // Ejecutar cálculo por empleado
      const resultados = [];

      for (const emp of listaEmpleados) {
        const payload = {
          empleado_id: emp.id,
          fecha_indicada: fechaIndicadaBackend,
          fecha_inicial: fechaInicial,
          fecha_final: fechaFinal,
          integracion_id: integracionId,
        };

        try {
          const res = await upsertAguinaldoEmpleado(payload);

          if (res?.ok && res.payload) {
            const vista = res.payload;

            resultados.push({
              empleado: vista.empleado_id,
              nombre: vista.nombre,
              fecha_ingreso: vista.fecha_ingreso
                ? dayjs(vista.fecha_ingreso).format("DD/MM/YYYY")
                : "",
              dias_considerar: vista.dias_considerar,
              dias_aguinaldo: vista.dias_aguinaldo,
              faltas: vista.faltas,
              incap: vista.incapacidades,
            });
          } else {
            message.error(`Error en cálculo para empleado ID ${emp.id}: ${res?.message || "Error desconocido"}`);
          }
        } catch (err) {
          message.error(`Error en cálculo para empleado ID ${emp.id}: ${err.message || "Error desconocido"}`);
        }
      }

      setRows(resultados);
    } catch (err) {
      message.error("Ocurrió un error al calcular el aguinaldo");
    } finally {
      setLoading(false);
    }
  };


  return (
    <PageApp
      routes={routes}
      title="Cálculo de Aguinaldo"
      description="Cálculo anual considerando antigüedad, ausentismos y filtros seleccionados."
    >
      {/* ---- PARTE SUPERIOR: CARDS ---- */}
      <Row gutter={24}>
        {/* Columna izquierda: parámetros + filtros */}
        <Col span={10}>
          <Card title="Parámetros generales" size="small" style={{ marginBottom: 16 }}>
            <Form layout="vertical" form={form}>
              <Form.Item label="Año" name="anio" initialValue={new Date().getFullYear()} rules={[{ required: true, message: 'El año es requerido' }]}>
                <Input />
              </Form.Item>

              <Form.Item label="Tabla de Integración a usar" name="integracion_id" rules={[{ required: true, message: 'La integración es requerida' }]}>
                <Select
                  placeholder="Selecciona una integración"
                  options={(integracion || []).map((i) => ({
                    value: i.id,
                    label: `${i.clave} - ${i.descripcion}`,
                  }))}
                />
              </Form.Item>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    label="Fecha Inicial"
                    name="fecha_inicial"
                    initialValue={dayjs().startOf('year')}
                    rules={[{ required: true, message: 'Selecciona la fecha inicial.' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Fecha Final"
                    name="fecha_final"
                    initialValue={dayjs().endOf('year')}
                    rules={[{ required: true, message: 'Selecciona la fecha final.' }]}
                  >
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Fecha para Antigüedad"
                name="fecha_antiguedad"
                initialValue="ingreso"
              >
                <Select>
                  <Select.Option value="ingreso">Ingreso (IMSS)</Select.Option>
                  <Select.Option value="antiguedad">Antigüedad</Select.Option>
                </Select>
              </Form.Item>
            </Form>
          </Card>

          <Card title="Configuración de ausentismos" size="small" style={{ marginBottom: 16 }}>
            <Collapse ghost>
              <Panel header="Tipos de ausentismos a considerar" key="1">
                <Row gutter={[8, 8]}>
                  {tiposAusentismo.map((item) => {
                    const key = `${item.source}-${item.id}`;
                    return (
                      <Col span={12} key={key}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text style={{ fontSize: 12 }}>
                            {item.nombre || item.tipo}
                          </Text>
                          <Switch
                            size="small"
                            checked={!!ausentismosSeleccionados[key]}
                            onChange={(checked) => handleChangeAusentismo(key, checked, item)}
                          />
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Panel>
            </Collapse>
          </Card>
        </Col>

        {/* Columna derecha: filtros */}
        <Col span={14}>
          <Card title="Filtros" size="small">
            <Form layout="vertical" form={form}>
              <Form.Item label="Rango Empleados" name="rango_empleados">
                <Input placeholder="Ej: 1,2,3,4" />
              </Form.Item>

              <Form.Item label="Departamento" name="departamento_id">
                <Select
                  allowClear
                  placeholder="Selecciona un departamento"
                  options={(departamentos || []).map((d) => ({
                    value: d.odoo_id,
                    label: d.text,
                  }))}
                />
              </Form.Item>

              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item label="Área" name="area_id">
                    <Select
                      allowClear
                      placeholder="Selecciona un área"
                      options={(areas || []).map((a) => ({
                        value: a.value,
                        label: a.text || a.name || a.descripcion,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Puesto" name="puesto">
                    <Select
                      allowClear
                      placeholder="Selecciona un puesto"
                      options={(puestos || []).map((p) => ({
                        value: p.odoo_id,
                        label: p.name,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row justify="space-between" style={{ marginTop: 12 }}>
                <Button type="primary" onClick={onCalculate} loading={loading}>
                  CALCULAR
                </Button>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>

      <Divider />

      {/* ---- PARTE INFERIOR: TABLA ---- */}
      <Card 
        title="Empleados calculados" 
        size="small" 
        extra={
          <Button 
            icon={<DownloadOutlined />}
            onClick={exportToExcel} 
            disabled={rows.length === 0}
          >
            Exportar a Excel
          </Button>
        }
      >
        <Table
          dataSource={rows}
          columns={columns}
          rowKey="empleado"
          bordered
          pagination={false}
          size="small"
          loading={loading}
          locale={{
            emptyText: (
              <span>
                No hay datos para mostrar. Presiona <strong>CALCULAR</strong> para obtener el
                cálculo de aguinaldo.
              </span>
            ),
          }}
        />
      </Card>
    </PageApp>
  );
}